import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Box,
    IconButton,
    Snackbar
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { AxiosWithAuth } from '../../Utilities/authenticationService';
import ButtonWithConfirmPrompt from '../ButtonWithConfirmPrompt';
import { PATHS } from '../../constants';

const AdminCompanyManagements = () => {
    const history = useHistory();
    const [companies, setCompanies] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [newlyCreatedCompany, setNewlyCreatedCompany] = useState(null);

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = () => {
        AxiosWithAuth.get('/companies')
            .then(res => {
                setCompanies(res.data);
            })
            .catch(err => {
                console.error(err);
                setSnackbar({ open: true, message: '会社一覧の取得に失敗しました', severity: 'error' });
            });
    };

    const handleOpenDialog = (company = null) => {
        if (company) {
            setEditingCompany(company);
            setCompanyName(company.company_name);
        } else {
            setEditingCompany(null);
            setCompanyName('');
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingCompany(null);
        setCompanyName('');
    };

    const handleSave = () => {
        if (!companyName.trim()) {
            setSnackbar({ open: true, message: '会社名を入力してください', severity: 'error' });
            return;
        }

        const data = { company: { company_name: companyName.trim() } };

        if (editingCompany) {
            // Update existing company
            AxiosWithAuth.put(`/companies/${editingCompany.id}`, data)
                .then(() => {
                    setSnackbar({ open: true, message: '会社を更新しました', severity: 'success' });
                    fetchCompanies();
                    handleCloseDialog();
                })
                .catch(err => {
                    const errorMessage = err.response?.data?.company_name?.[0] || '会社の更新に失敗しました';
                    setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                });
        } else {
            // Create new company
            AxiosWithAuth.post('/companies', data)
                .then((res) => {
                    const createdCompany = res.data;
                    setNewlyCreatedCompany(createdCompany);
                    setSnackbar({
                        open: true,
                        message: `${createdCompany.company_name}を登録しました。次は店舗を新会社に紐づけてください。`,
                        severity: 'success'
                    });
                    fetchCompanies();
                    handleCloseDialog();
                })
                .catch(err => {
                    const errorMessage = err.response?.data?.company_name?.[0] || '会社の作成に失敗しました';
                    setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                });
        }
    };

    const handleArchive = (company) => {
        AxiosWithAuth.post(`/companies/${company.id}/archive`)
            .then(() => {
                setSnackbar({ open: true, message: '会社を無効化しました', severity: 'success' });
                fetchCompanies();
            })
            .catch(err => {
                const errorMessage = err.response?.data?.error || '会社の無効化に失敗しました';
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            });
    };

    const isDefaultCompany = (company) => {
        return company.default_company === true;
    };

    return (
        <Box sx={{ margin: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: '800px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">会社管理</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                >
                    新規作成
                </Button>
            </Box>

            <Table sx={{ width: '100%', maxWidth: '800px' }}>
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>会社名</TableCell>
                        <TableCell align="right">操作</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {companies.map((company) => (
                        <TableRow key={company.id}>
                            <TableCell>{company.id}</TableCell>
                            <TableCell>{company.company_name}</TableCell>
                            <TableCell align="right">
                                <IconButton
                                    onClick={() => handleOpenDialog(company)}
                                    size="small"
                                >
                                    <Edit />
                                </IconButton>
                                {!isDefaultCompany(company) && (
                                    <ButtonWithConfirmPrompt
                                        component={
                                            <IconButton size="small">
                                                <Delete />
                                            </IconButton>
                                        }
                                        title="会社の無効化"
                                        content={`「${company.company_name}」を無効にしてよろしいですか？無効化された会社のユーザーはログインできなくなります。`}
                                        confirmText="無効化"
                                        handleConfirm={() => handleArchive(company)}
                                    />
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingCompany ? '会社を編集' : '会社を作成'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="会社名"
                        fullWidth
                        variant="outlined"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>キャンセル</Button>
                    <Button onClick={handleSave} variant="contained">
                        {editingCompany ? '更新' : '作成'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={newlyCreatedCompany ? null : 6000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => {
                    setSnackbar({ ...snackbar, open: false });
                    setNewlyCreatedCompany(null);
                }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => {
                        setSnackbar({ ...snackbar, open: false });
                        setNewlyCreatedCompany(null);
                    }}
                    action={
                        newlyCreatedCompany ? (
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    history.push({
                                        pathname: PATHS.adminStoreManagements,
                                        state: {
                                            newCompanyId: newlyCreatedCompany.id,
                                            newCompanyName: newlyCreatedCompany.company_name
                                        }
                                    });
                                    setSnackbar({ ...snackbar, open: false });
                                    setNewlyCreatedCompany(null);
                                }}
                            >
                                店舗管理へ
                            </Button>
                        ) : null
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminCompanyManagements;

